// Kid detail (R-B parent-mode restructure, Aug 2026) — the Kids tab's per-child page.
// Everything here was moved out of the old AdjustTab (ParentView.jsx) verbatim: the state,
// handlers and markup for Manage balances / Spending cap / Experience rewards are unchanged,
// just split into standalone components so they can mount under the new four-group layout
// (R-C polish, Aug 2026: Quick actions / How {kid} earns / What {kid} is saving for /
// Money & history — the audit's "Do now" group became the Quick actions row + the
// Add/deduct modal) instead of one long AdjustTab. GoalsSection/EarningActivitiesSection/
// DailyHabitsSection (AdjustSections.jsx) and LearningAdminTab are reused exactly as they
// already were — this file only supplies the pieces that used to live directly in AdjustTab.
import { DailyHabitsSection, EarningActivitiesSection, GoalsSection } from "../components/AdjustSections.jsx";
import { attributionName, deviceOwnerMap } from "../lib/adults.js";
import { AddSheet, TillSheet } from "../components/Till.jsx";
import { CollapsibleSection } from "../components/ChildView.jsx";
import { ActivityList, ActivityRow, CoinIcon, EditPencilIcon, Icon, VaultIcon } from "../components/Common.jsx";
import { apiGetDevices } from "../lib/api.js";
import { useBackLayer } from "../lib/backstack.js";
import { isUndoableOinkGift } from "../lib/oinks.js";
import { GiveOinks, LearningAdminTab } from "../components/LearningAdminTab.jsx";
import { SpendingReview } from "../components/SpendingReview.jsx";
import { repriceToCap } from "../lib/spendingDefaults.js";
import { CURRENCY_SYMBOL, DEFAULT_SPENDING_CAP_GBP, uid } from "../lib/constants.js";
import { activityRowStyle, earnedThisWeekPence, fmtActivityStamp, fmtPence, fmtTimestamp, spendingCapPence } from "../lib/utils.js";
import { t as ct } from "../lib/content.js";
import { ChildLearnEarnPane } from "./AdaptiveHub.jsx";
const { useState, useEffect } = React;

// Group header — Heading-step navy type on the canvas (docs/DESIGN_GUIDANCE.md §5): section
// headers are type, not tracked caps.
function GroupLabel({ children }) {
  return <div className="text-sm font-semibold text-slate-900 px-1 pt-2">{children}</div>;
}

/* ------------------------------ Do now ------------------------------ */
// Moved verbatim from AdjustTab's "🧾 Manage balances" CollapsibleSection — same state,
// same submit() validation, same three-way Add/Deduct/Spend toggle. R-C polish (Aug 2026):
// no longer a collapsible section in the page flow — the "Do now" group is gone and this
// form now lives inside the Add/deduct modal (see KidDetail below), reached from the Kids
// tab's Quick actions and from Home's Spend/Add quick actions. Still deliberately the ONLY
// "do something right now" surface until the Till task builds the real one.
// onComplete (Alan, 22 Sep): a quick action's pop-up closes once the act is done and its
// toast is up, like Spend and Add already did. Only on success: a refused form stays open.
export function ManageBalancesForm({ child, addTransaction, onComplete }) {
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState("");
  const [dir, setDir] = useState("credit");
  const [balanceType, setBalanceType] = useState("token");
  const [error, setError] = useState("");

  const DIR_LABELS = balanceType === "vault"
    ? { credit: "Add to Savings Vault", debit: "Deduct from Savings Vault", spend: "Record spend (Savings Vault)" }
    : { credit: "Add tokens", debit: "Deduct tokens", spend: "Record spend" };

  function submit() {
    const raw = parseFloat(amount);
    if (!reason.trim()) { setError("A reason is required."); return; }
    if (!raw || raw <= 0) { setError(balanceType === "vault" ? `Enter a ${CURRENCY_SYMBOL} amount greater than zero.` : "Enter a token amount greater than zero."); return; }
    setError("");
    const amt = balanceType === "vault" ? Math.round(raw * 100) : Math.round(raw);
    addTransaction(child.id, amt, reason, dir, balanceType);
    setReason(""); setAmount("");
    if (onComplete) onComplete();
  }

  return (
    <div>
      <div className="flex rounded-lg overflow-hidden border border-slate-200 mb-2 w-fit">
        <button className={`px-3 py-1.5 text-xs font-bold flex items-center gap-1 ${balanceType === "token" ? "bg-slate-900 text-white" : "bg-white text-slate-500"}`} onClick={() => setBalanceType("token")}><CoinIcon className="w-3 h-3" /> {ct("kid_detail.tokens_label", "Tokens")}</button>
        <button className={`px-3 py-1.5 text-xs font-bold ${balanceType === "vault" ? "bg-slate-900 text-white" : "bg-white text-slate-500"}`} onClick={() => setBalanceType("vault")}><VaultIcon className="w-3.5 h-3.5 inline-block" /> {ct("kid_detail.vault_label", "Savings Vault")}</button>
      </div>
      <div className="flex rounded-lg overflow-hidden border border-slate-200 mb-3">
        <button className={`flex-1 py-2 text-sm font-bold ${dir === "credit" ? "bg-slate-900 text-white" : "bg-white text-slate-500"}`} onClick={() => setDir("credit")}>{ct("kid_detail.add_tab", "+ Add")}</button>
        <button className={`flex-1 py-2 text-sm font-bold ${dir === "debit" ? "bg-slate-900 text-white" : "bg-white text-slate-500"}`} onClick={() => setDir("debit")}>{ct("kid_detail.deduct_tab", "− Deduct")}</button>
        <button className={`flex-1 py-2 text-sm font-bold ${dir === "spend" ? "bg-slate-900 text-white" : "bg-white text-slate-500"}`} onClick={() => setDir("spend")}>{ct("kid_detail.spend_tab", "Spend")}</button>
      </div>
      <p className="text-base text-slate-400 mb-2">{ct("kid_detail.deduct_vs_spend", "Deduct is for losing balance (e.g. behaviour); Spend is for using it on something, tracked separately.")}</p>
      <div className="flex items-center gap-2 mb-2">
        <label className="text-base font-semibold text-slate-500 w-24 flex-shrink-0">{ct("kid_detail.explanation_label", "Explanation")}</label>
        <input className="border border-slate-200 rounded-lg px-3 py-2 text-base flex-1 min-w-0" value={reason} onChange={(e) => setReason(e.target.value)} />
      </div>
      <div className="flex items-center gap-2 mb-2">
        <label className="text-base font-semibold text-slate-500 w-24 flex-shrink-0">{ct("kid_detail.amount_label", "Amount")}</label>
        <input className="border border-slate-200 rounded-lg px-3 py-2 text-base flex-1 min-w-0" placeholder={balanceType === "vault" ? `${CURRENCY_SYMBOL} amount` : "Token amount"} type="number" step={balanceType === "vault" ? "0.01" : "1"} value={amount} onChange={(e) => setAmount(e.target.value)} />
      </div>
      {error && <div className="text-rose-600 text-base font-semibold mb-2">{error}</div>}
      <button className="w-full font-bold text-sm rounded-lg py-2.5 text-white bg-slate-900" onClick={submit}>{DIR_LABELS[dir]}</button>
    </div>
  );
}

/* --------------------------- How {kid} earns --------------------------- */
// Moved verbatim from AdjustTab's spending-cap card (unconditional, not a CollapsibleSection —
// matches the prototype, which also shows it always-open under "How {kid} earns").
// R-C polish (Aug 2026): now a collapsed row inside the "How {kid} earns" group card —
// summary line carries the cap + this week's usage; the toggle and amount input live in the
// expanded body. data-testid stays on the wrapper so 10-spending-cap.spec.js scopes to it
// (that spec now expands the row first — an enumerated R-C spec edit).
export function SpendingCapCard({ child, state, update, open, onToggle, showToast }) {
  const rate = state.settings?.exchangeRate > 0 ? state.settings.exchangeRate : 10;
  const [capAmountDraft, setCapAmountDraft] = useState(null);

  function toggleSpendingCap() {
    update((s) => {
      const c = s.profiles.find((p) => p.id === child.id);
      if (c) c.spendingCap = { ...(c.spendingCap || { weeklyGBP: DEFAULT_SPENDING_CAP_GBP }), enabled: !(c.spendingCap?.enabled !== false) };
      return s;
    });
  }
  // Changing the cap changes what every reward is worth AS A SHARE of the week, which is
  // the only thing that makes a reward "too high" or "too low" — so this is exactly the
  // moment to offer a resize (Alan, 30 Aug 2026). Offered, never automatic: the cap is
  // already saved by the time the card appears, and "Leave them" does nothing.
  const [capResizeOffer, setCapResizeOffer] = useState(false);
  function saveCapAmount(raw) {
    const n = parseFloat(raw);
    if (!n || n <= 0) return;
    const next = Math.round(n * 100) / 100;
    const previous = child.spendingCap?.weeklyGBP ?? null;
    update((s) => {
      const c = s.profiles.find((p) => p.id === child.id);
      if (c) c.spendingCap = { ...(c.spendingCap || {}), enabled: true, weeklyGBP: next };
      return s;
    });
    setCapAmountDraft(null);
    if (previous !== next) setCapResizeOffer(true);
  }
  function acceptCapResize() {
    setCapResizeOffer(false);
    let result = null;
    update((s) => { result = repriceToCap(s, child.id); return result && result.changed ? s : null; });
    if (!showToast || !result) return;
    if (!result.changed) showToast(ct("toast.reprice_nothing", "Nothing to rebalance — everything is already as low as it goes."), "info");
    else if (!result.fits) showToast((result.dropped && result.dropped.length ? ct("toast.reprice_trimmed_over", "Rebalanced and removed {list} — still {over} tokens over the cap. Lower a reward or remove a task to fit.", { list: result.dropped.join(", "), over: result.over }) : ct("toast.reprice_over", "Still {over} tokens over the cap — lower a reward or remove a task to fit.", { over: result.over, name: child.name })), "info");
    else showToast((result.dropped && result.dropped.length ? ct("toast.reprice_trimmed", "Rebalanced to fit {name}'s cap. Removed {list}", { name: child.name, list: result.dropped.join(", ") }) : ct("toast.repriced", "Rewards rebalanced to fit {name}'s cap", { name: child.name })), "good");
  }

  const capEnabled = child.spendingCap?.enabled !== false;
  const capWeeklyGBP = child.spendingCap?.weeklyGBP ?? DEFAULT_SPENDING_CAP_GBP;
  const earnedPence = earnedThisWeekPence(state.transactions, child.id, rate);
  const capPence = spendingCapPence(child);
  const pct = capPence ? Math.min(100, Math.round((earnedPence / capPence) * 100)) : 0;
  return (
    <div data-testid="spending-cap-card">
    <CollapsibleSection
      title="Spending cap"
      icon={<Icon id="ic-cap" size={18} hue="gold" />}
      variant="row"
      open={open}
      onToggle={onToggle}
      summary={<p className="text-xs text-slate-500">{capEnabled ? ct("kid_detail.cap_row_summary", "{cap} per week · {used} used", { cap: fmtPence(capPence), used: fmtPence(earnedPence) }) : ct("kid_detail.cap_row_off", "No weekly limit")}</p>}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-semibold text-slate-700">{ct("kid_detail.cap_body_toggle", "Weekly cap on")}</span>
        <button type="button" onClick={toggleSpendingCap} aria-pressed={capEnabled}
          className={`flex-shrink-0 inline-block w-9 h-5 rounded-full relative transition-colors ${capEnabled ? "bg-emerald-500" : "bg-slate-200"}`}>
          <span className={`absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${capEnabled ? "translate-x-4" : "translate-x-0"}`} />
        </button>
      </div>
      <p className="text-base text-slate-400 mb-3">Limit how much {child.name} can earn each week.</p>
      {capEnabled ? (
        <>
          <div className="flex items-center mb-3">
            <div className="flex items-center gap-2 ml-auto">
              <label className="text-base font-semibold text-slate-500 flex-shrink-0">{CURRENCY_SYMBOL} per week</label>
              <input
                className="border border-slate-200 rounded-lg px-3 py-1.5 text-base w-24 text-right"
                type="number" min="0.01" step="1"
                value={capAmountDraft ?? capWeeklyGBP}
                onChange={(e) => setCapAmountDraft(e.target.value)}
                onBlur={(e) => saveCapAmount(e.target.value)}
              />
            </div>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-1.5">
            <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>{fmtPence(earnedPence)} earned this week</span>
            <span>of {fmtPence(capPence)}</span>
          </div>
          {capResizeOffer && (
            <div className="mt-3 border-t border-slate-200 pt-3" data-testid="cap-resize-offer">
              <div className="text-sm font-bold text-teal-800 mb-1">{ct("kid_detail.cap_resize_title", "Resize {name}'s rewards to match?", { name: child.name })}</div>
              <p className="text-xs text-teal-700 mb-2">{ct("kid_detail.cap_resize_body", "Their reward amounts haven't moved, so they're now worth a different share of the week. Resizing scales them all to fit the new cap and keeps your balance between them.")}</p>
              <div className="flex gap-2">
                <button className="flex-1 bg-slate-900 text-white text-xs font-bold rounded-lg py-2" data-testid="cap-resize-accept" onClick={acceptCapResize}>{ct("kid_detail.cap_resize_accept", "Resize rewards")}</button>
                <button className="flex-1 bg-white border border-slate-200 text-slate-600 text-xs font-bold rounded-lg py-2" data-testid="cap-resize-decline" onClick={() => setCapResizeOffer(false)}>{ct("kid_detail.cap_resize_decline", "Leave them")}</button>
              </div>
            </div>
          )}
        </>
      ) : (
        <p className="text-base text-slate-400">No weekly limit — {child.name} can earn freely.</p>
      )}
    </CollapsibleSection>
    </div>
  );
}

/* ----------------------- What {kid} is saving for ----------------------- */
// Moved verbatim from AdjustTab's "🎁 Experience rewards" CollapsibleSection.
export function ExperienceRewardsSection({ child, state, update, open, onToggle, variant }) {
  const [rewardAddTarget, setRewardAddTarget] = useState(null);
  const [rewardEditTarget, setRewardEditTarget] = useState(null);
  const [rewardLabel, setRewardLabel] = useState("");
  const [rewardTokens, setRewardTokens] = useState("");
  const [editingRewardId, setEditingRewardId] = useState(null);
  const [editRewardLabel, setEditRewardLabel] = useState("");
  const [editRewardTokens, setEditRewardTokens] = useState("");

  const allRewards = state.experienceRewards || [];
  const globalRewards = allRewards.filter((r) => !r.childId);
  const childRewards = allRewards.filter((r) => r.childId === child.id);

  function addReward() {
    const tokens = parseInt(rewardTokens, 10);
    if (!rewardLabel.trim() || !tokens || tokens <= 0) return;
    update((s) => {
      if (!s.experienceRewards) s.experienceRewards = [];
      s.experienceRewards.push({ id: uid(), label: rewardLabel.trim(), tokens, childId: rewardAddTarget === "child" ? child.id : null });
      return s;
    });
    setRewardLabel(""); setRewardTokens(""); setRewardAddTarget(null);
  }
  function removeReward(id) { update((s) => { s.experienceRewards = (s.experienceRewards || []).filter((r) => r.id !== id); return s; }); if (editingRewardId === id) setEditingRewardId(null); }
  function startEditReward(r) { setEditingRewardId(r.id); setEditRewardLabel(r.label); setEditRewardTokens(String(r.tokens)); }
  function saveEditReward() {
    const tokens = parseInt(editRewardTokens, 10);
    if (!editRewardLabel.trim() || !tokens || tokens <= 0) return;
    update((s) => { s.experienceRewards = s.experienceRewards.map((r) => (r.id === editingRewardId ? { ...r, label: editRewardLabel.trim(), tokens } : r)); return s; });
    setEditingRewardId(null);
  }

  return (
    <CollapsibleSection
      title={ct("kid_detail.rewards_title", "Experience rewards")}
      icon={variant === "row" ? <Icon id="ic-gift" size={18} hue="teal" /> : undefined}
      variant={variant}
      open={open}
      onToggle={onToggle}
      summary={variant === "row" ? (
        <p className="text-xs text-slate-500">{ct("kid_detail.rewards_row_summary", "{count} rewards set up", { count: globalRewards.length + childRewards.length })}</p>
      ) : (
        <div>
          <p className="text-base text-slate-500">Non-monetary rewards {child.name} can redeem with tokens. Try things like: 30 mins extra screen time, choose dinner, stay up 15 mins late, pick the family movie.</p>
          <p className="text-xs text-slate-400 mt-0.5">{globalRewards.length + childRewards.length} set up</p>
        </div>
      )}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <div className="text-xs font-medium text-slate-400">{ct("kid_detail.for_everyone", "For everyone")}</div>
        <button className="text-xs font-bold text-emerald-600" onClick={() => { setRewardAddTarget((t) => (t === "global" ? null : "global")); setRewardEditTarget(null); }}>{rewardAddTarget === "global" ? "Cancel" : "+ Add"}</button>
        <button className="text-xs font-bold text-slate-500" onClick={() => { setRewardEditTarget((t) => (t === "global" ? null : "global")); setRewardAddTarget(null); setEditingRewardId(null); }}>{rewardEditTarget === "global" ? "Done" : "Edit"}</button>
      </div>
      <div className="flex flex-wrap gap-2 mb-2">
        {globalRewards.map((r) => (
          editingRewardId === r.id ? (
            <div key={r.id} className="border border-slate-200 rounded-xl p-2 bg-white w-full sm:w-48">
              <label className="text-xs font-medium text-slate-500 block">{ct("kid_detail.reward_name_label", "Reward Name")}</label>
              <input className="text-xs font-bold w-full border-b border-slate-200 focus:outline-none mb-1.5" value={editRewardLabel} onChange={(e) => setEditRewardLabel(e.target.value)} autoFocus />
              <label className="text-xs font-medium text-slate-500 block">{ct("kid_detail.reward_cost_label", "Tokens")}</label>
              <input className="text-xs font-bold w-full border-b border-slate-200 focus:outline-none" type="number" value={editRewardTokens} onChange={(e) => setEditRewardTokens(e.target.value)} />
              <div className="flex gap-1.5 mt-1.5">
                <button className="text-emerald-700 bg-emerald-50 text-xs font-bold px-2 py-1 rounded-lg" onClick={saveEditReward}>{ct("kid_detail.save_button", "Save")}</button>
                <button className="text-rose-500 bg-rose-50 text-xs font-bold px-2 py-1 rounded-lg" onClick={() => removeReward(r.id)}>{ct("kid_detail.delete_button", "Delete")}</button>
              </div>
            </div>
          ) : rewardEditTarget === "global" ? (
            <span key={r.id} className="relative inline-flex">
              <button onClick={() => startEditReward(r)} className="rounded-lg border border-dashed border-slate-300 bg-white text-slate-600 text-xs font-bold px-3 py-1.5 pr-5">
                <EditPencilIcon width={11} height={11} className="inline-block align-[-1px] mr-0.5" /> {r.label} · {r.tokens}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete "${r.label}"?`)) removeReward(r.id); }}
                className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-rose-500 text-white text-xs font-bold flex items-center justify-center shadow-md ring-2 ring-white leading-none"
                aria-label={`Delete ${r.label}`}
              >×</button>
            </span>
          ) : (
            <span key={r.id} className="rounded-lg border text-xs font-semibold px-3 py-1.5 bg-violet-50 border-violet-200 text-violet-700">
              {r.label} · {r.tokens}
            </span>
          )
        ))}
        {globalRewards.length === 0 && <span className="text-xs text-slate-300">{ct("kid_detail.none_yet", "None yet")}</span>}
      </div>
      {rewardAddTarget === "global" && (
        <div className="border border-slate-200 rounded-xl p-2 bg-white w-full sm:w-48 mb-3">
          <label className="text-xs font-medium text-slate-500 block">{ct("kid_detail.reward_name_label", "Reward Name")}</label>
          <input className="text-xs font-bold w-full border-b border-slate-200 focus:outline-none mb-1.5" value={rewardLabel} onChange={(e) => setRewardLabel(e.target.value)} />
          <label className="text-xs font-medium text-slate-500 block">{ct("kid_detail.reward_cost_label", "Tokens")}</label>
          <input className="text-xs font-bold w-full border-b border-slate-200 focus:outline-none" type="number" value={rewardTokens} onChange={(e) => setRewardTokens(e.target.value)} />
          <div className="flex gap-1.5 mt-1.5">
            <button className="text-emerald-700 bg-emerald-50 text-xs font-bold px-2 py-1 rounded-lg" onClick={addReward}>{ct("kid_detail.add_button", "Add")}</button>
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 mb-1.5">
        <div className="text-xs font-medium text-slate-400">Just for {child.name}</div>
        <button className="text-xs font-bold text-emerald-600" onClick={() => { setRewardAddTarget((t) => (t === "child" ? null : "child")); setRewardEditTarget(null); }}>{rewardAddTarget === "child" ? "Cancel" : "+ Add"}</button>
        <button className="text-xs font-bold text-slate-500" onClick={() => { setRewardEditTarget((t) => (t === "child" ? null : "child")); setRewardAddTarget(null); setEditingRewardId(null); }}>{rewardEditTarget === "child" ? "Done" : "Edit"}</button>
      </div>
      <div className="flex flex-wrap gap-2">
        {childRewards.map((r) => (
          editingRewardId === r.id ? (
            <div key={r.id} className="border border-slate-200 rounded-xl p-2 bg-white w-full sm:w-48">
              <label className="text-xs font-medium text-slate-500 block">{ct("kid_detail.reward_name_label", "Reward Name")}</label>
              <input className="text-xs font-bold w-full border-b border-slate-200 focus:outline-none mb-1.5" value={editRewardLabel} onChange={(e) => setEditRewardLabel(e.target.value)} autoFocus />
              <label className="text-xs font-medium text-slate-500 block">{ct("kid_detail.reward_cost_label", "Tokens")}</label>
              <input className="text-xs font-bold w-full border-b border-slate-200 focus:outline-none" type="number" value={editRewardTokens} onChange={(e) => setEditRewardTokens(e.target.value)} />
              <div className="flex gap-1.5 mt-1.5">
                <button className="text-emerald-700 bg-emerald-50 text-xs font-bold px-2 py-1 rounded-lg" onClick={saveEditReward}>{ct("kid_detail.save_button", "Save")}</button>
                <button className="text-rose-500 bg-rose-50 text-xs font-bold px-2 py-1 rounded-lg" onClick={() => removeReward(r.id)}>{ct("kid_detail.delete_button", "Delete")}</button>
              </div>
            </div>
          ) : rewardEditTarget === "child" ? (
            <span key={r.id} className="relative inline-flex">
              <button onClick={() => startEditReward(r)} className="rounded-lg border border-dashed border-slate-300 bg-white text-slate-600 text-xs font-bold px-3 py-1.5 pr-5">
                <EditPencilIcon width={11} height={11} className="inline-block align-[-1px] mr-0.5" /> {r.label} · {r.tokens}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete "${r.label}"?`)) removeReward(r.id); }}
                className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-rose-500 text-white text-xs font-bold flex items-center justify-center shadow-md ring-2 ring-white leading-none"
                aria-label={`Delete ${r.label}`}
              >×</button>
            </span>
          ) : (
            <span key={r.id} className="rounded-lg border text-xs font-semibold px-3 py-1.5 bg-violet-50 border-violet-200 text-violet-700">
              {r.label} · {r.tokens}
            </span>
          )
        ))}
        {childRewards.length === 0 && <span className="text-xs text-slate-300">{ct("kid_detail.none_yet", "None yet")}</span>}
      </div>
      {rewardAddTarget === "child" && (
        <div className="border border-slate-200 rounded-xl p-2 bg-white w-full sm:w-48 mt-2">
          <label className="text-xs font-medium text-slate-500 block">{ct("kid_detail.reward_name_label", "Reward Name")}</label>
          <input className="text-xs font-bold w-full border-b border-slate-200 focus:outline-none mb-1.5" value={rewardLabel} onChange={(e) => setRewardLabel(e.target.value)} />
          <label className="text-xs font-medium text-slate-500 block">{ct("kid_detail.reward_cost_label", "Tokens")}</label>
          <input className="text-xs font-bold w-full border-b border-slate-200 focus:outline-none" type="number" value={rewardTokens} onChange={(e) => setRewardTokens(e.target.value)} />
          <div className="flex gap-1.5 mt-1.5">
            <button className="text-emerald-700 bg-emerald-50 text-xs font-bold px-2 py-1 rounded-lg" onClick={addReward}>{ct("kid_detail.add_button", "Add")}</button>
          </div>
        </div>
      )}
    </CollapsibleSection>
  );
}

/* ------------------------------ full page ------------------------------ */
// R-C polish (Aug 2026) — the Kids tab per the approved redesign sketch: Quick actions
// replace the old "Do now" group (Add/deduct opens the Manage balances modal), and the three
// remaining intent groups render as single cards of collapsed rows (CollapsibleSection
// variant="row") with data-rich one-line summaries, hairline dividers between rows.
// KidDetail owns a per-section open map so Quick actions and Home's kid-specific quick
// actions (via `focus`, set by ParentView.goKidSection) can open + auto-scroll a section —
// the scrolling comes free from CollapsibleSection's controlled-open effect.
export function KidDetail({ child, state, update, addTransaction, reverseTransaction, code, showToast, focus, onFocusModalClosed, onSelectChild, hiddenIds }) {

  // W2-PR(iii) — "via {device}" on transactions (review 3.6 display half): resolve the
  // registry once per mount. Suffix renders only when the household has >1 known device
  // — on a single-device household every row would say the same thing and the suffix
  // becomes noise (P5: dispute context, not surveillance theatre). "system" rows
  // (Money Monday accruals) always label, so automatic money movement is never
  // mistaken for a person's action.
  const [deviceLabels, setDeviceLabels] = useState(null);
  useEffect(() => {
    let alive = true;
    apiGetDevices(code).then((d) => { if (alive) setDeviceLabels(d || {}); }).catch(() => {});
    return () => { alive = false; };
  }, [code]);
  // LB-343 (G2): every entry names the ADULT who did it, not the phone it was done on — on a
  // shared family phone those are different questions, and "who" is the one a parent is
  // asking. The fallback chain is the brief's: the name written at the time, then the adult
  // the row names, then the owner of the device it was written on, then "A parent". The
  // device's own label is no longer the answer here; Settings -> Devices is where a phone is
  // identified.
  function txnViaLabel(t) {
    if (!t.deviceId) return undefined;
    if (t.deviceId === "system") return ct("activity.via_system", "Money Monday");
    const who = attributionName(state, t, deviceOwnerMap(deviceLabels));
    if (who) return who;
    return ct("activity.by_parent", "A parent");
    if (!deviceLabels || Object.keys(deviceLabels).length < 2) return undefined;
    const rec = deviceLabels[t.deviceId];
    return rec ? (rec.customLabel || rec.label || t.deviceId.slice(0, 6)) : ct("activity.via_removed", "a removed device");
  }
  const recent = state.transactions.filter((t) => t.childId === child.id);
  const [openMap, setOpenMap] = useState({});
  // R-D (Aug 2026, punch-list 4+6): ONE modal state for every quick action — each opens its
  // component in a focus window; closing returns the parent exactly where they were. The
  // window is rendered through a PORTAL to document.body: ParentView's root carries the
  // `.pop` entrance animation (a transform keyframe), and a transform-animated ancestor is
  // a containing block for fixed-position descendants — the documented mobile-browser bug
  // class behind "overlay opened once, then taps go dead" (item 6). Portaling out of the
  // animated subtree removes the whole failure class rather than patching one symptom.
  const [modal, setModal] = useState(null); // null | "balances" | "activities" | "goals" | "learnearn" | "review"
  // Alan, 26 Sep (LB-364 staging): with a SHORT focus window open (a homework list's page),
  // a drag had nothing to scroll inside the window and moved the page behind it instead.
  // overscroll-contain only holds a gesture that started on scrollable content, so the page
  // itself is locked while any window is open, the way TestOverlay locks it for a test.
  useEffect(() => {
    if (!modal) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [modal]);
  const rowToggle = (id) => (v) => setOpenMap((m) => ({ ...m, [id]: v }));

  // R-E (Aug 2026, Alan's phone pass): a focus window opened BY a Home quick action closes
  // back to Home — the parent's mental model is "I did a thing from Home", so close means
  // done-with-the-thing, not "now browse this kid". Windows opened from THIS page's own
  // pills close in place. modalFromFocusRef tracks which of the two opened the current one.
  const modalFromFocusRef = React.useRef(false);
  React.useEffect(() => {
    if (!focus?.section) return;
    modalFromFocusRef.current = true;
    setModal(focus.section);
  }, [focus]);

  // Row-level focus (28 Aug, staging test H). `focus.section` opens one of the four quick
  // action MODALS; the spending cap isn't one of those — it's a collapsed row in the "How
  // {kid} earns" group — so Requests' "Adjust their cap →" landed a parent on the right
  // child with the cap still folded away, which reads as the link not having worked.
  // Opening the row is enough on its own: CollapsibleSection's controlled-open effect
  // scrolls it into view and moves focus there.
  React.useEffect(() => {
    if (!focus?.row) return;
    setOpenMap((m) => ({ ...m, [focus.row]: true }));
  }, [focus]);
  function openModal(id) { modalFromFocusRef.current = false; setModal(id); }
  // LB-315: every focus window (Add / deduct, Add activity, Add goal, Learn & Earn, Spending
  // review, the Till, Add) is one layer; back closes it by the same path as its X.
  useBackLayer(!!modal, () => closeModal());
  function closeModal() {
    const fromFocus = modalFromFocusRef.current;
    modalFromFocusRef.current = false;
    setModal(null);
    if (fromFocus && onFocusModalClosed) onFocusModalClosed();
  }

  const qa = [
    { id: "balances", icon: "ic-fix", hue: "gold", label: ct("kid_detail.qa_balances", "Add / deduct") },
    { id: "activities", icon: "ic-medal", hue: "gold", label: ct("kid_detail.qa_activities", "Add activity") },
    { id: "goals", icon: "ic-goals", hue: "teal", label: ct("kid_detail.qa_goals", "Add goal") },
    { id: "learnearn", icon: "ic-brain", hue: "indigo", label: ct("kid_detail.qa_learnearn", "Learn & Earn") },
    // Spending review (Aug 2026; a parent reads it as "Earnings check" since 2 Oct, Alan: the
    // screen is about what a child can EARN, not what they spent). Deliberately a quick action rather than a Settings-only
    // page: the question it answers — "is what I've set up still sensible for this child?" —
    // arises while looking at that child, not while looking at household settings.
    { id: "review", icon: "ic-cap", hue: "gold", label: ct("kid_detail.qa_review", "Earnings check") },
    // LB-247 (Alan, 10 Sep): the parent Give Oinks lever (LB-168) was buried under Learn &
    // Earn; it is a per-child act, so it lives in this row. The L&E entry stays as a second route.
    { id: "oinks", icon: "ic-gift", hue: "pink", label: ct("kid_detail.qa_oinks", "Give Oinks") },
  ];
  const MODAL_TITLES = {
    balances: ct("kid_detail.balances_modal_title", "Manage balances · {name}", { name: child.name }),
    till: ct("till.modal_title", "Spend · {name}", { name: child.name }),
    add: ct("add.modal_title", "Add · {name}", { name: child.name }),
    activities: "Earning activities",
    goals: "Goals",
    learnearn: "Learn & Earn",
    review: ct("review.modal_title", "Earnings check · {name}", { name: child.name }),
    oinks: ct("kid_detail.oinks_modal_title", "Give Oinks · {name}", { name: child.name }),
  };

  return (
    <div className="flex flex-col gap-2 lb-form">
      <GroupLabel>{ct("kid_detail.quick_actions", "Quick actions")}</GroupLabel>
      <div className="grid grid-cols-2 gap-2">
        {qa.map((a) => (
          <button key={a.id} onClick={() => openModal(a.id)} className="bg-white rounded-lg shadow-lg px-3 py-2.5 flex items-center gap-2.5 text-left active:scale-[0.98] transition-transform">
            <Icon id={a.icon} hue={a.hue} size={22} className="flex-shrink-0" />
            <span className="font-semibold text-slate-900 text-sm truncate">{a.label}</span>
          </button>
        ))}
      </div>

      <GroupLabel>{ct("kid_detail.group_earns", "How {name} earns tokens", { name: child.name })}</GroupLabel>
      <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-200">
        <DailyHabitsSection child={child} state={state} update={update} variant="row" open={!!openMap.habits} onToggle={rowToggle("habits")} />
        {/* LB-19 Zip B (screen 4, Alan: "Good"): Just for fun lives with the other things you
            manage for this child, out of Learn & Earn. Same body as before, re-housed. */}
        <CollapsibleSection title="Just for fun" testid="kd-row-fun" icon={<Icon id="ic-arcade" size={18} hue="indigo" />} variant="row" open={!!openMap.fun} onToggle={rowToggle("fun")} summary={<p className="text-xs text-slate-500">{ct("kid_detail.fun_row_summary", "Games without tokens or tests")}</p>}>
          <LearningAdminTab state={state} update={update} activeChild={child} showToast={showToast} funOnly />
        </CollapsibleSection>
        <EarningActivitiesSection child={child} state={state} update={update} addTransaction={addTransaction} variant="row" open={!!openMap.activities} onToggle={rowToggle("activities")} />
        <CollapsibleSection title="Learn & Earn" testid="kd-row-learnearn" icon={<Icon id="ic-brain" size={18} hue="indigo" />} variant="row" open={!!openMap.learnearn} onToggle={rowToggle("learnearn")} summary={<p className="text-xs text-slate-500">{ct("kid_detail.learn_earn_row_summary", "Spelling, vocabulary and maths for {name}", { name: child.name })}</p>}>
          {(() => {
            // LB-19 Zip B v2 (Alan): an Adaptive child's row opens the ADAPTIVE view — the
            // Manual view is one deliberate tap away inside the pane. A fully Manual child
            // goes straight to LearningAdminTab as before.
            return <ChildLearnEarnPane state={state} update={update} showToast={showToast} child={child} />;
          })()}
        </CollapsibleSection>
        <SpendingCapCard showToast={showToast} child={child} state={state} update={update} open={!!openMap.cap} onToggle={rowToggle("cap")} />
      </div>

      <GroupLabel>{ct("kid_detail.group_saving_for", "What {name} is saving for", { name: child.name })}</GroupLabel>
      <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-200">
        <GoalsSection child={child} state={state} update={update} addTransaction={addTransaction} code={code} variant="row" rowTestId="kd-row-goals" open={!!openMap.goals} onToggle={rowToggle("goals")} />
        <ExperienceRewardsSection child={child} state={state} update={update} variant="row" open={!!openMap.rewards} onToggle={rowToggle("rewards")} />
      </div>

      <GroupLabel>{ct("kid_detail.group_money_history", "Money and history")}</GroupLabel>
      <div className="bg-white rounded-lg border border-slate-200">
        <CollapsibleSection title="Recent activity" icon={<Icon id="ic-recent" size={18} />} variant="row" open={!!openMap.recent} onToggle={rowToggle("recent")} summary={<p className="text-xs text-slate-500">{recent.length === 0 ? ct("kid_detail.recent_row_empty", "No transactions yet") : ct("kid_detail.recent_row_summary", "{count} transactions · last {when}", { count: recent.length, when: fmtTimestamp(recent[0].timestamp) })}</p>}>
          <ActivityList
            items={recent}
            emptyText="No transactions yet."
            renderRow={(t) => {
              const style = activityRowStyle(t);
              const undoable = !t.status || t.status === "approved";
              return (
                <ActivityRow
                  key={t.id}
                  {...style}
                  label={t.reason}
                  viaLabel={txnViaLabel(t)}
                  timestamp={fmtActivityStamp(t.timestamp)}
                  status={t.status}
                  reversed={t.reversed}
                  isReversal={!!t.reversalOf}
                  // LB-356 (Alan, 21 Sep): an Oinks gift is undone in Oinks (lib/oinks.js
                  // reverseOinkGift), so Undo is back on those rows; any other Oinks row has no
                  // undo rule and gets none. LB-355: a rate-change resize is not undoable on its
                  // own, because putting the count back without the rate is a third state
                  // nobody chose. Changing the rate again is the way back.
                  onUndo={reverseTransaction && undoable && !t.reversed && !t.reversalOf && !t.rateResize && (t.balance !== "oinks" || isUndoableOinkGift(t)) ? () => reverseTransaction(t.id) : undefined}
                />
              );
            }}
          />
        </CollapsibleSection>
      </div>

      {modal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" role="dialog" aria-label={modal === "balances" ? "Manage balances" : modal === "till" ? "Spend" : modal === "add" ? "Add" : MODAL_TITLES[modal]}>
          {/* Backdrop is NOT a dismiss target (Alan, 28 Aug): these focus windows hold
              in-progress edits (Till, Add, Manage balances), so only the ✕ closes them. */}
          {/* Header pinned, body scrolls (Alan, 30 Aug 2026). Two reported problems, one
              cause: the whole window was a single overflow-y-auto box, so on a long modal
              (Spending review, Learn & Earn) the title and ✕ scrolled away — leaving no
              visible way out — and on iOS the touch that ran off the end of the inner box
              was handed to the PAGE behind, which then scrolled instead. Splitting into a
              flex column with a fixed header and one scrolling body fixes both: the way out
              is always on screen, and `overscroll-contain` stops the gesture escaping to the
              background. touch-action: pan-y keeps the body itself scrollable on iOS. */}
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 pt-4 pb-3 flex-shrink-0 border-b border-slate-100 bg-white">
              <span className="text-base font-bold text-slate-900">{MODAL_TITLES[modal]}</span>
              <button className="text-slate-400 leading-none px-1" aria-label="Close" onClick={closeModal} data-testid="modal-close"><Icon id="ic-close" size={18} /></button>
            </div>
            <div className="overflow-y-auto overscroll-contain px-4 pb-4 pt-3 flex-1 min-h-0" style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }} data-testid="modal-body">
            {modal === "balances" && <ManageBalancesForm child={child} addTransaction={addTransaction} onComplete={closeModal} />}
            {modal === "till" && <TillSheet child={child} state={state} addTransaction={addTransaction} onComplete={closeModal} update={update} />}
            {modal === "add" && <AddSheet child={child} addTransaction={addTransaction} onComplete={closeModal} />}
            {modal === "activities" && <EarningActivitiesSection child={child} state={state} update={update} addTransaction={addTransaction} code={code} variant="row" open={true} onToggle={() => {}} onComplete={closeModal} />}
            {modal === "goals" && <GoalsSection child={child} state={state} update={update} addTransaction={addTransaction} code={code} variant="row" open={true} onToggle={() => {}} onComplete={closeModal} />}
            {modal === "learnearn" && (() => {
            // LB-19 Zip B v2 (Alan): an Adaptive child's row opens the ADAPTIVE view — the
            // Manual view is one deliberate tap away inside the pane. A fully Manual child
            // goes straight to LearningAdminTab as before.
            return <ChildLearnEarnPane state={state} update={update} showToast={showToast} child={child} />;
          })()}
            {/* Earnings check switches child in place (Alan, 3 Oct): the pills move the Kids tab's
                own selected child, so this window, its title and the page behind all follow,
                and closing lands on the child last reviewed. Keyed by child so a half-typed
                row on one child never carries over to the next. */}
            {modal === "review" && <SpendingReview key={child.id} child={child} state={state} update={update} showToast={showToast} profiles={state.profiles} onSwitchChild={onSelectChild} hiddenIds={hiddenIds} />}
            {modal === "oinks" && <GiveOinks child={child} update={update} showToast={showToast} onComplete={closeModal} />}
            </div>
          </div>
        </div>,
        w_doc_body()
      )}
    </div>
  );
}

// Indirection so the portal target is resolved at render time (SSR-free app, always a DOM).
function w_doc_body() { return document.body; }
